import React, { useState, useEffect } from "react";
import { Helmet } from "react-helmet-async";
import { useNavigate } from "react-router-dom";
import { supabase } from "../../utils/supabaseClient";
import {
  Box,
  Typography,
  TextField,
  Button,
  CircularProgress,
  Paper,
  Divider,
} from "@mui/material";
import LockResetIcon from "@mui/icons-material/LockReset";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";

function withTimeout(promise, ms = 8000, label = "request") {
  return Promise.race([
    promise,
    new Promise((_, reject) =>
      setTimeout(() => reject(new Error(`${label}_timeout`)), ms)
    ),
  ]);
}

function parseHashTokens() {
  // Supports links like:
  // https://your.site/reset-password#access_token=...&refresh_token=...&type=recovery
  const hash = window.location.hash?.startsWith("#")
    ? window.location.hash.slice(1)
    : "";

  const params = new URLSearchParams(hash);
  const access_token = params.get("access_token");
  const refresh_token = params.get("refresh_token");
  const type = params.get("type");
  return { access_token, refresh_token, type };
}

const ResetPassword = () => {
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [error, setError] = useState("");
  const [message, setMessage] = useState("");
  const [loading, setLoading] = useState(false);
  const [passwordUpdated, setPasswordUpdated] = useState(false);
  const [checkingLink, setCheckingLink] = useState(true);

  const navigate = useNavigate();

  useEffect(() => {
    let mounted = true;

    const initRecoverySession = async () => {
      if (!mounted) return;
      setError("");
      setCheckingLink(true);

      // Safety: never allow “verifying” forever
      const watchdog = setTimeout(() => {
        if (!mounted) return;
        setCheckingLink(false);
        setError(
          "Verification is taking too long. The link may be blocked/expired. Please request a new reset email."
        );
      }, 9000);

      try {
        const url = new URL(window.location.href);
        const code = url.searchParams.get("code");

        // 1) PKCE flow: ?code=...
        if (code) {
          await withTimeout(
            supabase.auth.exchangeCodeForSession(code),
            8000,
            "exchangeCodeForSession"
          );

          // Clean URL so refresh doesn’t re-run exchange
          url.searchParams.delete("code");
          window.history.replaceState({}, document.title, url.toString());
        } else {
          // 2) Legacy hash flow: #access_token=...&refresh_token=...&type=recovery
          const { access_token, refresh_token, type } = parseHashTokens();
          if (access_token && refresh_token && type === "recovery") {
            await withTimeout(
              supabase.auth.setSession({ access_token, refresh_token }),
              8000,
              "setSession"
            );

            // Clean hash
            window.history.replaceState(
              {},
              document.title,
              window.location.pathname + window.location.search
            );
          }
        }

        // 3) Confirm session exists
        const {
          data: { session },
        } = await withTimeout(supabase.auth.getSession(), 8000, "getSession");

        if (!session) {
          throw new Error("no_session");
        }

        if (!mounted) return;
        setCheckingLink(false);
        setError("");
      } catch (e) {
        console.error("[ResetPassword] initRecoverySession error:", e);

        if (!mounted) return;
        setCheckingLink(false);

        // Friendly error variants
        if (String(e?.message).includes("timeout")) {
          setError(
            "Verification timed out. Please request a new reset email (or try in an incognito window / disable adblock)."
          );
        } else {
          setError(
            "Reset link is invalid or expired. Please request a new password reset email."
          );
        }
      } finally {
        clearTimeout(watchdog);
      }
    };

    initRecoverySession();

    return () => {
      mounted = false;
    };
  }, []);

  const handleResetPassword = async () => {
    setError("");
    setMessage("");
    setLoading(true);

    try {
      if (password !== confirmPassword) {
        throw new Error("Passwords do not match.");
      }
      if (password.length < 6) {
        throw new Error("Password must be at least 6 characters long.");
      }

      // Hard guard: if no session, don't hang
      const {
        data: { session },
      } = await withTimeout(supabase.auth.getSession(), 8000, "getSession");

      if (!session) {
        throw new Error(
          "Reset session missing/expired. Please request a new reset email."
        );
      }

      const { error: updateError } = await withTimeout(
        supabase.auth.updateUser({ password }),
        8000,
        "updateUser"
      );

      if (updateError) throw updateError;

      setMessage("Password updated successfully!");
      setPasswordUpdated(true);

      // Optional cleanup
      try {
        await supabase.auth.signOut({ scope: "local" });
      } catch (_) {}

      setTimeout(() => navigate("/"), 2500);
    } catch (err) {
      console.error("[ResetPassword] error:", err);
      setError(err?.message || "An unexpected error occurred.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
    <Helmet>
      <meta name="robots" content="noindex, nofollow" />
      <title>Reset Password | FlyBnB</title>
    </Helmet>
    <Box
      sx={{
        minHeight: "80vh",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        p: 2,
      }}
    >
      <Paper
        elevation={6}
        sx={{
          maxWidth: 450,
          width: "100%",
          p: 4,
          borderRadius: 4,
          background: "#1e1e1e",
          color: "#fff",
          border: "1px solid #333",
          boxShadow: "0 8px 32px rgba(0, 0, 0, 0.4)",
        }}
      >
        {checkingLink ? (
          <Box sx={{ textAlign: "center", py: 4 }}>
            <CircularProgress size={28} color="inherit" />
            <Typography sx={{ mt: 2, color: "text.secondary" }}>
              Verifying reset link...
            </Typography>
          </Box>
        ) : !passwordUpdated ? (
          <>
            <Box sx={{ textAlign: "center", mb: 3 }}>
              <Box
                sx={{
                  display: "inline-flex",
                  p: 1.5,
                  borderRadius: "50%",
                  bgcolor: "rgba(95, 90, 162, 0.15)",
                  color: "#7e78d2",
                  mb: 2,
                }}
              >
                <LockResetIcon sx={{ fontSize: 40 }} />
              </Box>
              <Typography variant="h5" fontWeight="800" gutterBottom>
                Reset Password
              </Typography>
              <Typography variant="body2" sx={{ color: "text.secondary" }}>
                Enter your new password below.
              </Typography>
            </Box>

            <Divider sx={{ borderColor: "#333", mb: 4 }} />

            <TextField
              fullWidth
              label="New Password"
              type="password"
              variant="outlined"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              sx={{ mb: 3 }}
              disabled={!!error}
            />
            <TextField
              fullWidth
              label="Confirm New Password"
              type="password"
              variant="outlined"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              sx={{ mb: 3 }}
              disabled={!!error}
            />

            {error && (
              <Paper
                sx={{
                  bgcolor: "rgba(211, 47, 47, 0.1)",
                  color: "#ff8a80",
                  p: 1.5,
                  mb: 3,
                  borderRadius: 2,
                  border: "1px solid rgba(211, 47, 47, 0.3)",
                  textAlign: "center",
                  fontSize: "0.9rem",
                  fontWeight: 500,
                }}
              >
                {error}
              </Paper>
            )}

            <Button
              variant="contained"
              onClick={handleResetPassword}
              disabled={loading || !!error}
              fullWidth
              size="large"
              sx={{
                py: 1.5,
                borderRadius: 2,
                textTransform: "none",
                fontWeight: "bold",
                fontSize: "1rem",
                color: "#fff",
                background: `linear-gradient(45deg, #5F5AA2, #3d3a7a)`,
                boxShadow: "0 4px 14px rgba(95, 90, 162, 0.4)",
              }}
            >
              {loading ? (
                <CircularProgress size={26} color="inherit" />
              ) : (
                "Set New Password"
              )}
            </Button>

            {error && (
              <Button
                variant="text"
                fullWidth
                sx={{ mt: 2, textTransform: "none", color: "#aaa" }}
                onClick={() => navigate("/")}
              >
                Go back home
              </Button>
            )}
          </>
        ) : (
          <Box sx={{ textAlign: "center", py: 4 }}>
            <CheckCircleIcon sx={{ fontSize: 60, color: "#4caf50", mb: 3 }} />
            <Typography variant="h5" fontWeight="bold" gutterBottom>
              All Set!
            </Typography>
            <Typography variant="body1" sx={{ color: "text.secondary", mb: 3 }}>
              {message}
            </Typography>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                gap: 1,
                color: "#666",
              }}
            >
              <CircularProgress size={16} color="inherit" />
              <Typography variant="caption">Redirecting home...</Typography>
            </Box>
          </Box>
        )}
      </Paper>
    </Box>
    </>
  );
};

export default ResetPassword;
